import { eAtlas, eFontAsset, eManulSortLayerDrawOrder, eSfxSprite, Game } from "../../../Game";
import { AtlasUtils } from "../../../Utils/Atlas/AtlasUtils";
import { CircleFx, CircleFxSettings } from "../../CircleFx";
import { RoundManager } from "../../Round/RoundManager";
import { ePhysicsObjects } from "../../Stadium/Stadium";
import { eTargetState, TargetBase } from "./Target";
import { TargetManager } from "../TargetManager";
import { TargetType } from "../TargetType";
import { Ball } from "../../Player/Ball";



export class RoundTarget extends TargetBase {
    /**
     * Velikost stredu terce pro detekci BULL'S EYE (0 az 100)
     */
    static readonly CENTER_RADIUS = 25;
    /**
     * Extra skore za zasah do stredu
     */
    static readonly CENTER_BONUS_SCORE = 50;
    /**
     * Fragment shader chunk, ktery umoznuje pres uFillRatio nastavit jaka cast terce ma byt vyplnena barvou a jaka ma byt zasedla
     */
    /*    private static readonly diffuseShaderChunk = `
            uniform sampler2D texture_diffuseMap;
            uniform float uMinUvY;
            uniform float uMaxUvY;
            uniform float uFillRatio;
    
            void getAlbedo() {
                dAlbedo = vec3(1.0);
                dAlbedo *= gammaCorrectInput(addAlbedoDetail(texture2D(texture_diffuseMap, $UV).$CH));
    
                //float minY = 0.3828125;
                //float maxY = 0.7578125;
                //float filingRatio = 0.25;
    
                float grayFactor = (1.0 - step(uMinUvY + (1.0 - uFillRatio) * (uMaxUvY - uMinUvY), $UV.y)) * 0.75;
                float gray = 0.21 * dAlbedo.r + 0.71 * dAlbedo.g + 0.07 * dAlbedo.b;
    
                dAlbedo = dAlbedo * (1.0 - grayFactor) + (gray * grayFactor);
            }`;*/
    private static readonly emissiveShaderChunk = `
        uniform float uMinUvY;
        uniform float uMaxUvY;
        uniform float uFillRatio;

        #ifdef MAPCOLOR
        uniform vec3 material_emissive;
        #endif

        #ifdef MAPFLOAT
        uniform float material_emissiveIntensity;
        #endif

        #ifdef MAPTEXTURE
        uniform sampler2D texture_emissiveMap;
        #endif

        vec3 getEmission() {
            vec3 emission = vec3(1.0);

            #ifdef MAPFLOAT
            emission *= material_emissiveIntensity;
            #endif

            #ifdef MAPCOLOR
            emission *= material_emissive;
            #endif

            #ifdef MAPTEXTURE
            emission *= $texture2DSAMPLE(texture_emissiveMap, $UV).$CH;
            #endif

            #ifdef MAPVERTEX
            emission *= gammaCorrectInput(saturate(vVertexColor.$VC));
            #endif

            float grayFactor = (1.0 - step(uMinUvY + (1.0 - uFillRatio) * (uMaxUvY - uMinUvY), $UV.y));// * 1.0;
            //float gray = 0.21 * emission.r + 0.71 * emission.g + 0.07 * emission.b;
            float gray = 0.3 * emission.r + 0.59 * emission.g + 0.11 * emission.b;

            return emission * (1.0 - grayFactor) + (gray * grayFactor);
        }`;

    /**
     * Minimalni polomer terce
     */
    private static readonly MIN_DIAMETER = 1;
    /**
     * Maximalni polomer terce
     */
    private static readonly MAX_DIAMETER = 2.4;
    /**
     * Polomer terce pri nejnizsi obtiznosti
     */
    private static readonly MIN_DIF_DIAMETER = 1.75;

    private static readonly DEF_RADIUS = 0.5;
    private static readonly MULTIPLIER_NUM_OPACITY = 1;
    /**
     * Barva outliny pro jednotlive verze terce
     */
    private static readonly MUTLIPLIER_OUTLINE_COLOR: pc.Color[] = [
        new pc.Color(0, 0, 0),
        new pc.Color().fromString("#540200")
    ];

    /**
     * Pomer naplneni terce barvou (0 = terc je cely sedy, 1 = terc je cely vybarveny)
     */
    set fillRatio(value: number) {
        if (value < 0)
            value = 0;

        if (value > 1)
            value = 1;

        if (value != this._fillRatio) {
            this._fillRatio = value;
            this._material.setParameter("uFillRatio", value);
        }
    }
    get fillRatio(): number { return this._fillRatio; }
    private _fillRatio: number;

    /**
     * Entita pro zobrazeni hodnoty nasobice
     */
    private _multiplierEnt: pc.Entity;
    /**
     * Kruhovy efekt okolo terce
     */
    private _circleFx: CircleFx;
    /**
     * Verze terce. 0 = normalni, 1 = zlata
     */
    private _version: number;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(mng: TargetManager, type: TargetType) {
        super(mng, type);

        // Aktualizovat material terce
        let frameRect = AtlasUtils.getAtlasFrameRegion(Game.getAtlas(eAtlas.def1), type.frameKey);

        this._material.chunks.emissivePS = RoundTarget.emissiveShaderChunk;
        this._material.setParameter("uMinUvY", 1 - frameRect.y - frameRect.w);
        this._material.setParameter("uMaxUvY", 1 - frameRect.y);
        this._material.setParameter("uFillRatio", 1);
        this._material.update();

        // Verze terce
        this._version = 0;

        // Detekce kolize
        this._entity.addComponent(pc.eCompomentType.collision, {
            type: pc.eCollisionVolumeType.cylinder,
            radius: RoundTarget.DEF_RADIUS,
            height: 0.2,
            axis: 1
        });
        this._entity.collision.on("triggerenter", this.handleTriggerEnter, this);

        // Entita pro zobrazeni hodnoty multiplieru
        this._multiplierEnt = new pc.Entity();
        this._multiplierEnt.addComponent(pc.eCompomentType.element, {
            type: pc.ELEMENTTYPE_TEXT,
            fontSize: 0.4,
            fontAsset: Game.app.assets.find(eFontAsset.default, pc.eAssetType.font).id,
            autoWidth: true,
            autoHeight: true,
            pivot: [0.5, 0.5],
            color: new pc.Color(1, 1, 1),
            outlineColor: RoundTarget.MUTLIPLIER_OUTLINE_COLOR[0],
            outlineThickness: 0.5,
            opacity: RoundTarget.MULTIPLIER_NUM_OPACITY,
            layers: this._entity.model.layers
        });
        this._multiplierEnt.setLocalPosition(0, 0, 0.002);
        this._container.addChild(this._multiplierEnt);

        // Kruhovy efekt okolo terce
        this._circleFx = new CircleFx(new CircleFxSettings({
            innerRadius: 0.44,
            innerGradientSize: 0.02,
            outerGradientSize: 0.02,
            color: new pc.Color(1, 1, 1),
        }, {
            duration: 1250,
            scaleFrom: 1,
            scaleTo: 1.3,
            scaleEase: pc.SineOut,
            opacityFrom: 0.5,
            opacityTo: 0,
            opacityEase: pc.SineIn
        }, 800, 1, this._container, new pc.Vec3(0, 0, -0.002), new pc.Vec3(90, 0, 0), 0, this._entity.model.layers[0], false));
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): void {
        super.reset();
        this._circleFx.reset();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): void {
        if (this._state != eTargetState.hidden) {
            super.update();
            this._circleFx.update();
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    protected init(): void {
        let drawOrder = eManulSortLayerDrawOrder.target + this._id;

        // Graficka verze
        let version = Ball.instance.fireBallState != 1 ? 0 : 1;

        if (version != this._version) {
            let frameKey = this._type.frameKey;

            this._version = version;
            this.setAtlasFrame(frameKey.substring(0, frameKey.length - 1) + version);
            this._multiplierEnt.element.outlineColor = RoundTarget.MUTLIPLIER_OUTLINE_COLOR[version];
        }

        // Velikost
        let rndMng = RoundManager.instance;
        let diameter = (RoundTarget.MIN_DIF_DIAMETER - (RoundTarget.MIN_DIF_DIAMETER - RoundTarget.MIN_DIAMETER) * rndMng.roundDifficultyIncRatio) * this._settings.scale;

        if (rndMng.roundSetup.mainTargetScaling)    // Pokud se bude menit velikost terce, tak ho zvetsit
            diameter *= 1.25;

        // Za kazdy pokus zvetsit o X%
        //diameter += diameter * (0.15 * rndAttemptId);

        if (diameter < RoundTarget.MIN_DIAMETER) {
            diameter = RoundTarget.MIN_DIAMETER;
        }
        else if (diameter > RoundTarget.MAX_DIAMETER) {
            diameter = RoundTarget.MAX_DIAMETER;
        }

        this._width = this._height = diameter;

        // Hodnota nasobice
        this._scoreMultiplier = this._mng.scoreMultiplier;

        if ((this._multiplierEnt.enabled = this._scoreMultiplier > 1)) {
            this._multiplierEnt.element.text = this._scoreMultiplier + "x";
            this._multiplierEnt.element.drawOrder = drawOrder + 0.2;
            this._multiplierEnt.enabled = true;
        }
        else {
            this._multiplierEnt.enabled = false;
        }

        // Kruhovy efekt
        this._circleFx.settings.drawOrder = drawOrder;
        this._circleFx.reset();
        this._circleFx.start();

        // Poradi vykreslovani terce
        this._entity.model.meshInstances[0].drawOrder = drawOrder + 0.1;

        // Naplneni terce
        this.fillRatio = 1;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    protected updateScale(): void {
        this._entity.collision.radius = ((this._width / 2) * this._scale);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    protected updateOpacity(): void {
        this._circleFx.settings.opacity = this._opacity;
        this._multiplierEnt.element.opacity = this._opacity * RoundTarget.MULTIPLIER_NUM_OPACITY;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private handleTriggerEnter(other: pc.Entity): void {
        if (this._opacity < 0.25 || other.name != ePhysicsObjects.ball || this._state != eTargetState.idle)
            return;

        let targetPos = this._container.getPosition();
        let ballPos = other.getPosition();
        let hitEntry = this.calculateHit(new pc.Vec2(targetPos.x, targetPos.y), new pc.Vec2(ballPos.x, ballPos.y));

        if (!hitEntry)
            return;

        // Zobrazit animaci ulomku terce
        this._mng.activateShards(this._version, this);
        this._entity.enabled = false;
        this._multiplierEnt.enabled = false;

        // Zvuk
        Game.playSound(eSfxSprite.targetHit);

        // Ukoncit kruhovy efekt okolo terce
        this._circleFx.stop();

        // Animace zmizeni
        this.startHitAnim();

        // Predat udalost "triggerenter" mici
        other.collision.fire("triggerenter", this._entity);
    }
}